import { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Download, X, FileText, CheckCircle2, Lock, ClipboardList, Shield } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors, formatDate } from "./utils";

function LegalDocumentsContent({ projectId, vendorContracts, clientAgreements, completionChecklists, onRefresh, canWrite, token }) {
  const [activeTab, setActiveTab] = useState("vendor");
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Vendor Contract Form Data
  const defaultVendorForm = {
    effective_date: new Date().toISOString().split('T')[0],
    vendor_name: "", vendor_address: "", vendor_phone: "", vendor_email: "",
    designer_name: "Brillen Interiors", designer_address: "", designer_phone: "", designer_email: "",
    services: [""],
    payment_method: "Bank Transfer", payment_schedule: "As per milestones", total_payment: "",
    end_date: "", termination_notice_days: 30, governing_law: "India", notes: ""
  };
  const [vendorForm, setVendorForm] = useState(defaultVendorForm);

  // Client Agreement Form Data
  const defaultClientForm = {
    client_name: "", client_company: "", client_address: "", client_phone: "", client_email: "",
    project_address: "", project_type: "Residential", project_type_other: "", project_budget: "",
    start_date: "", completion_date: "",
    services_concept_development: false, services_space_planning: false, services_furniture_selection: false,
    services_lighting_design: false, services_materials_finishes: false, services_color_consultation: false, services_styling_decor: false,
    additional_procurement: false, additional_project_management: false, additional_3d_visualization: false, additional_custom_furniture: false, additional_other: "",
    design_fee: "", procurement_fee: "", project_management_fee: "", additional_services_fee: "", total_project_fee: "",
    deposit_amount: "", deposit_due_date: "", progress_payment1_amount: "", progress_payment1_due_date: "",
    progress_payment2_amount: "", progress_payment2_due_date: "", final_payment_amount: "", final_payment_due_date: "", notes: ""
  };
  const [clientForm, setClientForm] = useState(defaultClientForm);

  // Completion Checklist Form Data
  const defaultChecklistForm = {
    client_name: "", project_location: "",
    design_all_elements_installed: false, design_quality_check: false, design_furniture_placement: false, design_lighting_installed: false,
    walkthrough_scheduled: false, walkthrough_feedback_addressed: false, walkthrough_client_approval: false,
    doc_contracts_complete: false, doc_receipts_invoices: false, doc_maintenance_instructions: false,
    handover_keys_access: false, handover_warranties_manuals: false, handover_followup_scheduled: false,
    final_inspection_complete: false, final_safety_standards: false, final_deliverables_confirmed: false,
    notes: ""
  };
  const [checklistForm, setChecklistForm] = useState(defaultChecklistForm);

  const resetVendorForm = () => { setVendorForm(defaultVendorForm); setEditingVendor(null); setShowVendorForm(false); };
  const resetClientForm = () => { setClientForm(defaultClientForm); setEditingClient(null); setShowClientForm(false); };
  const resetChecklistForm = () => { setChecklistForm(defaultChecklistForm); setEditingChecklist(null); setShowChecklistForm(false); };

  const handleVendorServiceChange = (index, value) => {
    const newServices = [...vendorForm.services];
    newServices[index] = value;
    setVendorForm({ ...vendorForm, services: newServices });
  };

  const addVendorService = () => setVendorForm({ ...vendorForm, services: [...vendorForm.services, ""] });
  const removeVendorService = (index) => {
    if (vendorForm.services.length > 1) {
      setVendorForm({ ...vendorForm, services: vendorForm.services.filter((_, i) => i !== index) });
    }
  };

  const handleEditVendor = async (contract) => {
    try {
      const res = await axios.get(`${API}/legal-documents/vendor-contracts/${contract.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorForm(res.data);
      setEditingVendor(contract);
      setShowVendorForm(true);
    } catch (error) {
      toast.error("Failed to load contract");
    }
  };

  const handleEditClient = async (agreement) => {
    try {
      const res = await axios.get(`${API}/legal-documents/client-agreements/${agreement.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientForm(res.data);
      setEditingClient(agreement);
      setShowClientForm(true);
    } catch (error) {
      toast.error("Failed to load agreement");
    }
  };

  const handleEditChecklist = async (checklist) => {
    try {
      const res = await axios.get(`${API}/legal-documents/completion-checklists/${checklist.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklistForm(res.data);
      setEditingChecklist(checklist);
      setShowChecklistForm(true);
    } catch (error) {
      toast.error("Failed to load checklist");
    }
  };

  const handleSubmitVendor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...vendorForm, project_id: projectId, services: vendorForm.services.filter(s => s.trim()) };
      if (editingVendor) {
        await axios.put(`${API}/legal-documents/vendor-contracts/${editingVendor.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Contract updated!");
      } else {
        await axios.post(`${API}/legal-documents/vendor-contracts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Contract created!");
      }
      resetVendorForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contract");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClient = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...clientForm, project_id: projectId };
      if (editingClient) {
        await axios.put(`${API}/legal-documents/client-agreements/${editingClient.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agreement updated!");
      } else {
        await axios.post(`${API}/legal-documents/client-agreements`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agreement created!");
      }
      resetClientForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agreement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitChecklist = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...checklistForm, project_id: projectId };
      if (editingChecklist) {
        await axios.put(`${API}/legal-documents/completion-checklists/${editingChecklist.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Checklist updated!");
      } else {
        await axios.post(`${API}/legal-documents/completion-checklists`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Checklist created!");
      }
      resetChecklistForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save checklist");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor contract?")) return;
    try {
      await axios.delete(`${API}/legal-documents/vendor-contracts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contract deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm("Delete this client agreement?")) return;
    try {
      await axios.delete(`${API}/legal-documents/client-agreements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Agreement deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteChecklist = async (id) => {
    if (!window.confirm("Delete this completion checklist?")) return;
    try {
      await axios.delete(`${API}/legal-documents/completion-checklists/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Checklist deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDownloadVendorPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/legal-documents/vendor-contracts/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vendor_Contract_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadClientPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/legal-documents/client-agreements/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Client_Agreement_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadChecklistPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/legal-documents/completion-checklists/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Completion_Checklist_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleMarkComplete = async (id) => {
    if (!window.confirm("Mark this checklist as complete? This action cannot be undone.")) return;
    try {
      await axios.post(`${API}/legal-documents/completion-checklists/${id}/mark-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Checklist marked as complete!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as complete");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="vendor" data-testid="tab-vendor">Vendor Contracts</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client">Client Agreements</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Completion Checklist</TabsTrigger>
        </TabsList>

        {/* VENDOR CONTRACTS TAB */}
        <TabsContent value="vendor">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-900">Vendor Contract Agreements</h4>
            {canWrite && !showVendorForm && (
              <Button onClick={() => setShowVendorForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-vendor-contract-btn">
                <Plus className="h-4 w-4 mr-2" />New Contract
              </Button>
            )}
          </div>

          {showVendorForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingVendor ? `Edit Contract - ${editingVendor.id}` : "New Vendor Contract"}</h4>
                <Button variant="ghost" size="sm" onClick={resetVendorForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitVendor} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Effective Date</Label><Input type="date" value={vendorForm.effective_date} onChange={e => setVendorForm({...vendorForm, effective_date: e.target.value})} required /></div>
                  <div><Label>End Date</Label><Input type="date" value={vendorForm.end_date} onChange={e => setVendorForm({...vendorForm, end_date: e.target.value})} required /></div>
                </div>
                <div className="border-t pt-4"><h5 className="font-medium text-slate-700 mb-2">Vendor Details</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Vendor Name</Label><Input value={vendorForm.vendor_name} onChange={e => setVendorForm({...vendorForm, vendor_name: e.target.value})} required /></div>
                    <div><Label>Vendor Email</Label><Input type="email" value={vendorForm.vendor_email} onChange={e => setVendorForm({...vendorForm, vendor_email: e.target.value})} /></div>
                    <div><Label>Vendor Phone</Label><Input value={vendorForm.vendor_phone} onChange={e => setVendorForm({...vendorForm, vendor_phone: e.target.value})} /></div>
                    <div><Label>Vendor Address</Label><Input value={vendorForm.vendor_address} onChange={e => setVendorForm({...vendorForm, vendor_address: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-t pt-4"><h5 className="font-medium text-slate-700 mb-2">Designer Details</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Designer Name</Label><Input value={vendorForm.designer_name} onChange={e => setVendorForm({...vendorForm, designer_name: e.target.value})} required /></div>
                    <div><Label>Designer Email</Label><Input type="email" value={vendorForm.designer_email} onChange={e => setVendorForm({...vendorForm, designer_email: e.target.value})} /></div>
                    <div><Label>Designer Phone</Label><Input value={vendorForm.designer_phone} onChange={e => setVendorForm({...vendorForm, designer_phone: e.target.value})} /></div>
                    <div><Label>Designer Address</Label><Input value={vendorForm.designer_address} onChange={e => setVendorForm({...vendorForm, designer_address: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-t pt-4"><h5 className="font-medium text-slate-700 mb-2">Scope of Work</h5>
                  {vendorForm.services.map((svc, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input value={svc} onChange={e => handleVendorServiceChange(idx, e.target.value)} placeholder={`Service ${idx+1}`} className="flex-1" />
                      {vendorForm.services.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeVendorService(idx)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addVendorService}><Plus className="h-4 w-4 mr-1" />Add Service</Button>
                </div>
                <div className="border-t pt-4"><h5 className="font-medium text-slate-700 mb-2">Payment Terms</h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Payment Method</Label><Input value={vendorForm.payment_method} onChange={e => setVendorForm({...vendorForm, payment_method: e.target.value})} /></div>
                    <div><Label>Payment Schedule</Label><Input value={vendorForm.payment_schedule} onChange={e => setVendorForm({...vendorForm, payment_schedule: e.target.value})} /></div>
                    <div><Label>Total Payment (Rs.)</Label><Input value={vendorForm.total_payment} onChange={e => setVendorForm({...vendorForm, total_payment: e.target.value})} required /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Termination Notice (Days)</Label><Input type="number" value={vendorForm.termination_notice_days} onChange={e => setVendorForm({...vendorForm, termination_notice_days: parseInt(e.target.value)})} /></div>
                  <div><Label>Governing Law</Label><Input value={vendorForm.governing_law} onChange={e => setVendorForm({...vendorForm, governing_law: e.target.value})} /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={vendorForm.notes} onChange={e => setVendorForm({...vendorForm, notes: e.target.value})} rows={2} /></div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingVendor ? "Update" : "Create"}</Button>
                  <Button type="button" variant="outline" onClick={resetVendorForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {vendorContracts.length === 0 && !showVendorForm ? (
            <div className="text-center py-12 text-slate-500"><Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No vendor contracts yet</p></div>
          ) : !showVendorForm && (
            <div className="space-y-3">
              {vendorContracts.map(c => (
                <Card key={c.id} className="p-4 border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-mono text-sm text-brillien-600">{c.id}</span><Badge className={statusColors[c.status] || "bg-slate-100"}>{c.status}</Badge></div>
                      <h4 className="font-semibold mt-1">{c.vendor_name}</h4>
                      <p className="text-xs text-slate-500">Created: {formatDate(c.created_at)} | Updated: {formatDate(c.updated_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      {canWrite && (c.status === "draft" || c.status === "Draft") && <Button variant="outline" size="sm" onClick={() => handleEditVendor(c)} className="text-blue-600"><Edit className="h-4 w-4 mr-1" />Edit</Button>}
                      <Button variant="outline" size="sm" onClick={() => handleDownloadVendorPdf(c.id)} className="text-brillien-600"><Download className="h-4 w-4 mr-1" />PDF</Button>
                      {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeleteVendor(c.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CLIENT AGREEMENTS TAB */}
        <TabsContent value="client">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-900">Client Agreement Forms</h4>
            {canWrite && !showClientForm && (
              <Button onClick={() => setShowClientForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-client-agreement-btn">
                <Plus className="h-4 w-4 mr-2" />New Agreement
              </Button>
            )}
          </div>

          {showClientForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingClient ? `Edit Agreement - ${editingClient.id}` : "New Client Agreement"}</h4>
                <Button variant="ghost" size="sm" onClick={resetClientForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitClient} className="space-y-4">
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Client Information</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Client Name *</Label><Input value={clientForm.client_name} onChange={e => setClientForm({...clientForm, client_name: e.target.value})} required /></div>
                    <div><Label>Company</Label><Input value={clientForm.client_company} onChange={e => setClientForm({...clientForm, client_company: e.target.value})} /></div>
                    <div><Label>Phone</Label><Input value={clientForm.client_phone} onChange={e => setClientForm({...clientForm, client_phone: e.target.value})} /></div>
                    <div><Label>Email</Label><Input type="email" value={clientForm.client_email} onChange={e => setClientForm({...clientForm, client_email: e.target.value})} /></div>
                    <div className="col-span-2"><Label>Address</Label><Input value={clientForm.client_address} onChange={e => setClientForm({...clientForm, client_address: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Project Details</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2"><Label>Project Address</Label><Input value={clientForm.project_address} onChange={e => setClientForm({...clientForm, project_address: e.target.value})} /></div>
                    <div><Label>Project Type</Label>
                      <Select value={clientForm.project_type} onValueChange={v => setClientForm({...clientForm, project_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Residential">Residential</SelectItem><SelectItem value="Commercial">Commercial</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                      </Select>
                    </div>
                    {clientForm.project_type === "Other" && <div><Label>Specify Type</Label><Input value={clientForm.project_type_other} onChange={e => setClientForm({...clientForm, project_type_other: e.target.value})} /></div>}
                    <div><Label>Project Budget (Rs.)</Label><Input value={clientForm.project_budget} onChange={e => setClientForm({...clientForm, project_budget: e.target.value})} /></div>
                    <div><Label>Start Date</Label><Input type="date" value={clientForm.start_date} onChange={e => setClientForm({...clientForm, start_date: e.target.value})} /></div>
                    <div><Label>Completion Date</Label><Input type="date" value={clientForm.completion_date} onChange={e => setClientForm({...clientForm, completion_date: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Services</h5>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_concept_development} onChange={e => setClientForm({...clientForm, services_concept_development: e.target.checked})} /> Concept Development</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_space_planning} onChange={e => setClientForm({...clientForm, services_space_planning: e.target.checked})} /> Space Planning</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_furniture_selection} onChange={e => setClientForm({...clientForm, services_furniture_selection: e.target.checked})} /> Furniture Selection</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_lighting_design} onChange={e => setClientForm({...clientForm, services_lighting_design: e.target.checked})} /> Lighting Design</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_materials_finishes} onChange={e => setClientForm({...clientForm, services_materials_finishes: e.target.checked})} /> Materials & Finishes</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_color_consultation} onChange={e => setClientForm({...clientForm, services_color_consultation: e.target.checked})} /> Color Consultation</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.services_styling_decor} onChange={e => setClientForm({...clientForm, services_styling_decor: e.target.checked})} /> Styling & Decor</label>
                  </div>
                  <h6 className="font-medium text-slate-600 mt-3 mb-2 text-sm">Additional Services</h6>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.additional_procurement} onChange={e => setClientForm({...clientForm, additional_procurement: e.target.checked})} /> Procurement</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.additional_project_management} onChange={e => setClientForm({...clientForm, additional_project_management: e.target.checked})} /> Project Management</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.additional_3d_visualization} onChange={e => setClientForm({...clientForm, additional_3d_visualization: e.target.checked})} /> 3D Visualization</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={clientForm.additional_custom_furniture} onChange={e => setClientForm({...clientForm, additional_custom_furniture: e.target.checked})} /> Custom Furniture</label>
                    <div className="col-span-2"><Input placeholder="Other services..." value={clientForm.additional_other} onChange={e => setClientForm({...clientForm, additional_other: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Fee Structure (Rs.)</h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Design Fee *</Label><Input value={clientForm.design_fee} onChange={e => setClientForm({...clientForm, design_fee: e.target.value})} required /></div>
                    <div><Label>Procurement Fee</Label><Input value={clientForm.procurement_fee} onChange={e => setClientForm({...clientForm, procurement_fee: e.target.value})} /></div>
                    <div><Label>Project Mgmt Fee</Label><Input value={clientForm.project_management_fee} onChange={e => setClientForm({...clientForm, project_management_fee: e.target.value})} /></div>
                    <div><Label>Additional Services</Label><Input value={clientForm.additional_services_fee} onChange={e => setClientForm({...clientForm, additional_services_fee: e.target.value})} /></div>
                    <div><Label>Total Project Fee *</Label><Input value={clientForm.total_project_fee} onChange={e => setClientForm({...clientForm, total_project_fee: e.target.value})} required /></div>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Payment Schedule</h5>
                  <div className="grid grid-cols-4 gap-4">
                    <div><Label>Deposit *</Label><Input value={clientForm.deposit_amount} onChange={e => setClientForm({...clientForm, deposit_amount: e.target.value})} required /></div>
                    <div><Label>Deposit Due Date</Label><Input type="date" value={clientForm.deposit_due_date} onChange={e => setClientForm({...clientForm, deposit_due_date: e.target.value})} /></div>
                    <div><Label>Progress 1</Label><Input value={clientForm.progress_payment1_amount} onChange={e => setClientForm({...clientForm, progress_payment1_amount: e.target.value})} /></div>
                    <div><Label>Progress 1 Due</Label><Input type="date" value={clientForm.progress_payment1_due_date} onChange={e => setClientForm({...clientForm, progress_payment1_due_date: e.target.value})} /></div>
                    <div><Label>Progress 2</Label><Input value={clientForm.progress_payment2_amount} onChange={e => setClientForm({...clientForm, progress_payment2_amount: e.target.value})} /></div>
                    <div><Label>Progress 2 Due</Label><Input type="date" value={clientForm.progress_payment2_due_date} onChange={e => setClientForm({...clientForm, progress_payment2_due_date: e.target.value})} /></div>
                    <div><Label>Final Payment *</Label><Input value={clientForm.final_payment_amount} onChange={e => setClientForm({...clientForm, final_payment_amount: e.target.value})} required /></div>
                    <div><Label>Final Due Date</Label><Input type="date" value={clientForm.final_payment_due_date} onChange={e => setClientForm({...clientForm, final_payment_due_date: e.target.value})} /></div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={clientForm.notes} onChange={e => setClientForm({...clientForm, notes: e.target.value})} rows={2} /></div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingClient ? "Update" : "Create"}</Button>
                  <Button type="button" variant="outline" onClick={resetClientForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {clientAgreements.length === 0 && !showClientForm ? (
            <div className="text-center py-12 text-slate-500"><Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No client agreements yet</p></div>
          ) : !showClientForm && (
            <div className="space-y-3">
              {clientAgreements.map(a => (
                <Card key={a.id} className="p-4 border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-mono text-sm text-brillien-600">{a.id}</span><Badge className={statusColors[a.status] || "bg-slate-100"}>{a.status}</Badge></div>
                      <h4 className="font-semibold mt-1">{a.client_name}</h4>
                      <p className="text-sm text-slate-600">{a.project_type} | Budget: Rs. {a.project_budget}</p>
                      <p className="text-xs text-slate-500">Created: {formatDate(a.created_at)} | Updated: {formatDate(a.updated_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      {canWrite && (a.status === "draft" || a.status === "Draft") && <Button variant="outline" size="sm" onClick={() => handleEditClient(a)} className="text-blue-600"><Edit className="h-4 w-4 mr-1" />Edit</Button>}
                      <Button variant="outline" size="sm" onClick={() => handleDownloadClientPdf(a.id)} className="text-brillien-600"><Download className="h-4 w-4 mr-1" />PDF</Button>
                      {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeleteClient(a.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PROJECT COMPLETION CHECKLIST TAB */}
        <TabsContent value="checklist">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-900">Project Completion Checklists</h4>
            {canWrite && !showChecklistForm && (
              <Button onClick={() => setShowChecklistForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-checklist-btn">
                <Plus className="h-4 w-4 mr-2" />New Checklist
              </Button>
            )}
          </div>

          {showChecklistForm && (
            <Card className="p-5 mb-4 border-brillien-200 bg-brillien-50/50">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold">{editingChecklist ? `Edit Checklist - ${editingChecklist.id}` : "New Project Completion Checklist"}</h4>
                <Button variant="ghost" size="sm" onClick={resetChecklistForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={handleSubmitChecklist} className="space-y-4">
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Project Details</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Client Name *</Label><Input value={checklistForm.client_name} onChange={e => setChecklistForm({...checklistForm, client_name: e.target.value})} required /></div>
                    <div><Label>Project Location *</Label><Input value={checklistForm.project_location} onChange={e => setChecklistForm({...checklistForm, project_location: e.target.value})} required /></div>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Design Implementation</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.design_all_elements_installed} onChange={e => setChecklistForm({...checklistForm, design_all_elements_installed: e.target.checked})} className="h-4 w-4" /> All design elements installed as per plan</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.design_quality_check} onChange={e => setChecklistForm({...checklistForm, design_quality_check: e.target.checked})} className="h-4 w-4" /> Quality check on materials and finishes</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.design_furniture_placement} onChange={e => setChecklistForm({...checklistForm, design_furniture_placement: e.target.checked})} className="h-4 w-4" /> Furniture placement and arrangement completed</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.design_lighting_installed} onChange={e => setChecklistForm({...checklistForm, design_lighting_installed: e.target.checked})} className="h-4 w-4" /> Lighting fixtures installed and working properly</label>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Client Walkthrough</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.walkthrough_scheduled} onChange={e => setChecklistForm({...checklistForm, walkthrough_scheduled: e.target.checked})} className="h-4 w-4" /> Schedule a final walkthrough with the client</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.walkthrough_feedback_addressed} onChange={e => setChecklistForm({...checklistForm, walkthrough_feedback_addressed: e.target.checked})} className="h-4 w-4" /> Address any feedback or concerns from the client</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.walkthrough_client_approval} onChange={e => setChecklistForm({...checklistForm, walkthrough_client_approval: e.target.checked})} className="h-4 w-4" /> Obtain client approval on the completed project</label>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Documentation</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.doc_contracts_complete} onChange={e => setChecklistForm({...checklistForm, doc_contracts_complete: e.target.checked})} className="h-4 w-4" /> Complete project documentation, including all contracts and agreements</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.doc_receipts_invoices} onChange={e => setChecklistForm({...checklistForm, doc_receipts_invoices: e.target.checked})} className="h-4 w-4" /> Finalize and organize all receipts and invoices</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.doc_maintenance_instructions} onChange={e => setChecklistForm({...checklistForm, doc_maintenance_instructions: e.target.checked})} className="h-4 w-4" /> Prepare maintenance and care instructions for the client</label>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Project Handover</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.handover_keys_access} onChange={e => setChecklistForm({...checklistForm, handover_keys_access: e.target.checked})} className="h-4 w-4" /> Provide the client with keys and access to the space</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.handover_warranties_manuals} onChange={e => setChecklistForm({...checklistForm, handover_warranties_manuals: e.target.checked})} className="h-4 w-4" /> Hand over all warranties and manuals for installed products</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.handover_followup_scheduled} onChange={e => setChecklistForm({...checklistForm, handover_followup_scheduled: e.target.checked})} className="h-4 w-4" /> Schedule a follow-up meeting with the client for any post-project support</label>
                  </div>
                </div>
                <div className="border-b pb-4"><h5 className="font-medium text-slate-700 mb-2">Final Checks</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.final_inspection_complete} onChange={e => setChecklistForm({...checklistForm, final_inspection_complete: e.target.checked})} className="h-4 w-4" /> Conduct a final inspection of the project for any defects or issues</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.final_safety_standards} onChange={e => setChecklistForm({...checklistForm, final_safety_standards: e.target.checked})} className="h-4 w-4" /> Ensure all safety standards and regulations are met</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={checklistForm.final_deliverables_confirmed} onChange={e => setChecklistForm({...checklistForm, final_deliverables_confirmed: e.target.checked})} className="h-4 w-4" /> Confirm completion of all agreed-upon deliverables</label>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={checklistForm.notes} onChange={e => setChecklistForm({...checklistForm, notes: e.target.value})} rows={2} placeholder="Additional notes..." /></div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingChecklist ? "Update" : "Create"}</Button>
                  <Button type="button" variant="outline" onClick={resetChecklistForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {completionChecklists.length === 0 && !showChecklistForm ? (
            <div className="text-center py-12 text-slate-500"><ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No completion checklists yet</p></div>
          ) : !showChecklistForm && (
            <div className="space-y-3">
              {completionChecklists.map(c => {
                const totalItems = 16;
                const completedItems = [
                  c.design_all_elements_installed, c.design_quality_check, c.design_furniture_placement, c.design_lighting_installed,
                  c.walkthrough_scheduled, c.walkthrough_feedback_addressed, c.walkthrough_client_approval,
                  c.doc_contracts_complete, c.doc_receipts_invoices, c.doc_maintenance_instructions,
                  c.handover_keys_access, c.handover_warranties_manuals, c.handover_followup_scheduled,
                  c.final_inspection_complete, c.final_safety_standards, c.final_deliverables_confirmed
                ].filter(Boolean).length;
                const completionPct = Math.round((completedItems / totalItems) * 100);
                return (
                  <Card key={c.id} className={`p-4 ${c.status === 'completed' ? 'border-green-300 bg-green-50/50' : 'border-slate-200'}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-brillien-600">{c.id}</span>
                          <Badge className={c.status === 'completed' ? 'bg-green-100 text-green-700' : (statusColors[c.status] || "bg-slate-100")}>{c.status}</Badge>
                          {c.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                        </div>
                        <h4 className="font-semibold mt-1">{c.client_name}</h4>
                        <p className="text-sm text-slate-600">{c.project_location}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2"><div className={`h-2 rounded-full ${completionPct === 100 ? 'bg-green-500' : completionPct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{width: `${completionPct}%`}} /></div>
                          <span className="text-xs font-medium text-slate-600">{completedItems}/{totalItems} ({completionPct}%)</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Created: {formatDate(c.created_at)} | Updated: {formatDate(c.updated_at)}
                          {c.completed_at && <span className="text-green-600 font-medium"> | Completed: {formatDate(c.completed_at)}</span>}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {canWrite && (c.status === "draft" || c.status === "Draft") && completionPct === 100 && (
                          <Button size="sm" onClick={() => handleMarkComplete(c.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="mark-complete-btn">
                            <CheckCircle2 className="h-4 w-4 mr-1" />Mark Complete
                          </Button>
                        )}
                        {canWrite && (c.status === "draft" || c.status === "Draft") && <Button variant="outline" size="sm" onClick={() => handleEditChecklist(c)} className="text-blue-600"><Edit className="h-4 w-4 mr-1" />Edit</Button>}
                        <Button variant="outline" size="sm" onClick={() => handleDownloadChecklistPdf(c.id)} className="text-brillien-600"><Download className="h-4 w-4 mr-1" />PDF</Button>
                        {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDeleteChecklist(c.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ TIMELINE CONTENT ============

export { LegalDocumentsContent };
